import type { QueuedServerEvent } from "./global-sdk"

type Delta = Extract<QueuedServerEvent["payload"], { type: "message.part.delta" }>["properties"]
type Slot = { event: QueuedServerEvent; active: boolean; end?: number }
const encoder = new TextEncoder()

/** Coalesces only within a delta run; lifecycle events remain ordering barriers. */
export function createServerEventQueue() {
  let slots: Slot[] = []
  const deltas = new Map<string, Slot>()
  const replacements = new Map<string, Slot>()
  let bytes = 0
  return {
    get length() { return slots.length },
    // Match the server's replay limits before applying transport backpressure.
    get pressured() { return slots.length >= 256 || bytes >= 1024 * 1024 },
    push(event: QueuedServerEvent, replacementKey?: string) {
      if (event.payload.type === "message.part.delta") {
        const props = event.payload.properties as Delta & { byteOffset?: number }
        const size = encoder.encode(props.delta).length
        bytes += size
        const key = `${event.directory}:${props.messageID}:${props.partID}:${props.field}`
        const previous = deltas.get(key)
        if (previous?.event.payload.type === "message.part.delta" && previous.end === props.byteOffset) {
          previous.event.payload.properties.delta += props.delta
          if (previous.end !== undefined) previous.end += size
          return
        }
        const slot: Slot = {
          active: true,
          event: { ...event, payload: { ...event.payload, properties: { ...props } } },
          end: props.byteOffset === undefined ? undefined : props.byteOffset + size,
        }
        deltas.set(key, slot)
        slots.push(slot)
        return
      }
      deltas.clear()
      const slot: Slot = { event, active: true }
      if (replacementKey) {
        const previous = replacements.get(replacementKey)
        if (previous) previous.active = false
        replacements.set(replacementKey, slot)
      }
      slots.push(slot)
    },
    take() {
      const result = slots.filter((slot) => slot.active).map((slot) => slot.event)
      this.clear()
      return result
    },
    clear() {
      slots = []
      deltas.clear()
      replacements.clear()
      bytes = 0
    },
  }
}
